import React from "react";
import { useOutletContext } from "react-router-dom";
import { collection, getDocs, addDoc, updateDoc, doc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { books_upsertBook, books_adjustReceived } from "../../lib/firestoreAdapters";

export default function BooksDeliveries() {
  const { campus, schoolYear, setCampus, setSchoolYear, years } = useOutletContext();
  const [list, setList] = React.useState([]);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), itemsCSV: "" });

  async function reload() {
    const snap = await getDocs(collection(db, "books_receipts"));
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.campus === campus && r.schoolYear === schoolYear);
    setList(rows);
  }
  React.useEffect(() => { reload(); }, [campus, schoolYear]);

  function parseCSV(text) {
    const rows = text.split(/\r?\n/).filter(Boolean);
    if (!rows.length) return [];
    const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
    return rows.slice(1).map(line => {
      const cols = line.split(",").map(c => c.trim());
      const r = {}; headers.forEach((h, i) => r[h] = cols[i] || ""); return r;
    });
  }
  async function createReceipt(e) {
    e.preventDefault();
    const items = parseCSV(form.itemsCSV).map(r => ({
      isbn: r.isbn || "",
      title: r.title || "",
      grade: r.grade || "",
      publisher: r.publisher || form.supplier || "",
      qty: Number(r.qty || r.quantity || 0),
      cost: Number(r.cost || 0),
    })).filter(it => it.isbn || it.title);
    const rec = {
      campus, schoolYear,
      supplier: form.supplier,
      drNo: form.drNo,
      date: form.date,
      status: "To Receive",
      items,
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(db, "books_receipts"), rec);
    setShowNew(false);
    setForm({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), itemsCSV: "" });
    await reload();
    alert("Delivery logged in 'To Receive'. Move to 'Received' when verified to update inventory.");
  }
  async function moveStatus(row, status) {
    const prevStatus = row.status || "To Receive";
    await updateDoc(doc(db, "books_receipts", row.id), { status, updatedAt: new Date().toISOString() });
    // If moving to Received, increment inventory.received
    if (status === "Received" && prevStatus !== "Received") {
      try {
        for (const it of (row.items || [])) {
          const book = {
            title: it.title || "",
            isbn: it.isbn || "",
            publisher: it.publisher || row.supplier || "",
            grade: it.grade || "",
            cost: Number(it.cost || 0),
            price: Number(it.price || it.cost || 0),
            received: 0,
            sold: 0
          };
          const id = await books_upsertBook(campus, schoolYear, book);
          await books_adjustReceived(campus, schoolYear, id, Number(it.qty || 0));
        }
      } catch (e) {
        console.warn("Failed to apply inventory increments for receipt", row.id, e);
      }
    }
    await reload();
  }

  const cols = ["To Receive","Received","Reconciled"];
  const grouped = {};
  for (const c of cols) grouped[c] = list.filter(r => (r.status || "To Receive") === c);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Deliveries — {campus.toUpperCase()} — {schoolYear}</h5>
          {setCampus && (
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)} style={{ width: 140 }}>
              <option value="gapan">gapan</option>
              <option value="bulacan">bulacan</option>
            </select>
          )}
          {setSchoolYear && (
            <select className="form-select" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ width: 160 }}>
              {(years || []).map(y => <option key={y} value={y}>{y === "all" ? "All Years" : y}</option>)}
            </select>
          )}
        </div>
        <button className="btn btn-success" onClick={() => setShowNew(true)}>New Delivery</button>
      </div>
      <div className="row g-3">
        {cols.map(col => (
          <div key={col} className="col-md-4">
            <div className="card h-100">
              <div className="card-header fw-bold">{col}</div>
              <div className="card-body">
                {grouped[col].length === 0 ? (
                  <div className="text-muted small">No records</div>
                ) : grouped[col].map(r => (
                  <div key={r.id} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between">
                      <div>
                        <div className="fw-bold">{r.supplier}</div>
                        <div className="small text-muted">DR#: {r.drNo || "-"} • {r.date}</div>
                      </div>
                      <select className="form-select form-select-sm" style={{ width: 140 }} value={r.status || "To Receive"} onChange={e => moveStatus(r, e.target.value)}>
                        {cols.map(x => <option key={x}>{x}</option>)}
                      </select>
                    </div>
                    <div className="small mt-2">
                      {Array.isArray(r.items) ? r.items.slice(0,3).map(it => <div key={it.isbn || it.title}>{it.title || it.isbn} × {it.qty}</div>) : null}
                      {Array.isArray(r.items) && r.items.length > 3 && <div className="text-muted">+{r.items.length - 3} more…</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(900px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">New Delivery</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowNew(false)}>Close</button>
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={createReceipt}>
                  <div className="col-md-4">
                    <label className="form-label">Supplier/Publisher</label>
                    <input className="form-control" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">DR / Invoice #</label>
                    <input className="form-control" value={form.drNo} onChange={e => setForm(f => ({ ...f, drNo: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Date</label>
                    <input type="date" className="form-control" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Items CSV</label>
                    <textarea className="form-control" rows="6" placeholder="isbn,title,grade,publisher,cost,qty" value={form.itemsCSV} onChange={e => setForm(f => ({ ...f, itemsCSV: e.target.value }))}></textarea>
                    <div className="form-text">Paste CSV with headers: isbn,title,grade,publisher,cost,qty</div>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowNew(false)}>Cancel</button>
                    <button className="btn btn-primary">Create</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
